import { HEN_BREEDS } from '../data/breeds';
import { BROOD_SUCCESS, DEFAULT_BROOD_SUCCESS, DEFAULT_TRAIT_POOL, MAX_TRAITS, TRAIT_POOLS, type TraitId } from '../data/misc';
import {
  BROOD_DURATION_SEC, BROOD_FAIL_CONSOLATION, BROOD_REWARD_FLOOR, BROOD_REWARD_MULT, BROODY_AUTO_ACCEPT_SEC,
  MAX_BROOD_EGGS_ON_SCREEN, MAX_LAY_RATE,
} from '../data/balance';
import { layingPhaseMs, type ActionResult } from './actions';
import { earn } from './economy';
import { anyFrenzy, spawnEvent } from './events';
import { henMods } from './modifiers';
import { pickWeighted, rand } from './rng';
import { emit, findFlock, logActivity, touch } from './state';
import type { GameEvent, GameState, SimCtx } from './types';

/**
 * Broody lifecycle (v1.4 design): broody-prone hens alternate a laying phase
 * (2–6 min by broodiness × rooster broodyMod) with an 8-minute brood.
 * A flock needs its own rooster for hens to go broody. Broody hens lay
 * nothing; a successful brood grants a matriarch trait and a brood egg.
 */

export function tickBroody(s: GameState, ctx: SimCtx): void {
  const now = s.time;
  if (!anyFrenzy(s)) {
    for (const f of s.flocks) {
      if (!f.rooster) continue;
      f.boxes.forEach((b, i) => {
        const hen = b.hen;
        if (!hen || hen.broodySince !== null || !HEN_BREEDS[hen.breed].broodyProne) return;
        if (hen.layingUntil === null || now < hen.layingUntil) return;
        hen.broodySince = now;
        spawnEvent(s, ctx, 'broody', f.id, { boxIndex: i, decideBy: now + BROODY_AUTO_ACCEPT_SEC * 1000, broodUntil: null });
        touch(s);
      });
    }
  }
  for (const e of [...s.events]) {
    if (e.kind !== 'broody') continue;
    if ((e.broodUntil ?? null) === null && now >= (e.decideBy ?? 0)) startBrood(s, ctx, e);
    if (e.broodUntil && now >= e.broodUntil) resolveBrood(s, ctx, e);
  }
}

function startBrood(s: GameState, ctx: SimCtx, e: GameEvent): void {
  const hen = findFlock(s, e.flockId)?.boxes[e.boxIndex ?? -1]?.hen;
  const mods = hen ? henMods(s, ctx, hen) : null;
  const durationMs = BROOD_DURATION_SEC * 1000 * (mods?.broodDuration ?? 1);
  e.broodUntil = s.time + durationMs;
  e.expiresAt = e.broodUntil;
}

export function broodyChoice(s: GameState, ctx: SimCtx, eventId: number, choice: 'brood' | 'shoo'): ActionResult {
  const e = s.events.find((x) => x.id === eventId && x.kind === 'broody');
  if (!e) return { ok: false, reason: 'Gone' };
  if (e.broodUntil) return { ok: false, reason: 'Already brooding' };
  if (choice === 'brood') {
    startBrood(s, ctx, e);
    return { ok: true, amount: 0, detail: 'She settles in for an 8-minute sit.' };
  }
  const flock = findFlock(s, e.flockId);
  const hen = flock?.boxes[e.boxIndex ?? -1]?.hen;
  if (flock && hen) {
    hen.broodySince = null;
    hen.layingUntil = s.time + layingPhaseMs(hen.breed, flock);
  }
  s.events.splice(s.events.indexOf(e), 1);
  touch(s);
  return { ok: true, amount: 0, detail: 'Shoo! Back to laying.' };
}

function rollTraits(s: GameState, breed: keyof typeof HEN_BREEDS, have: TraitId[], count: number): TraitId[] {
  const got: TraitId[] = [];
  for (let i = 0; i < count && have.length + got.length < MAX_TRAITS; i++) {
    const pool = (TRAIT_POOLS[breed] ?? DEFAULT_TRAIT_POOL).filter(([t]) => !have.includes(t) && !got.includes(t));
    if (pool.length === 0) break;
    got.push(pickWeighted(s, pool));
  }
  return got;
}

function resolveBrood(s: GameState, ctx: SimCtx, e: GameEvent): void {
  s.events.splice(s.events.indexOf(e), 1);
  const flock = findFlock(s, e.flockId);
  const hen = flock?.boxes[e.boxIndex ?? -1]?.hen;
  if (!flock || !hen) return;
  const mods = henMods(s, ctx, hen);
  const deep = hen.traits.includes('DEEP_BROODER');
  const success = deep ? 1 : Math.min(1, (BROOD_SUCCESS[hen.breed] ?? DEFAULT_BROOD_SUCCESS) + mods.broodSuccess);
  if (rand(s) < success) {
    const traits = rollTraits(s, hen.breed, hen.traits, deep ? 2 : 1);
    hen.traits.push(...traits);
    hen.broods++;
    s.stats.broodyHatches++;
    // Pays a multiple of what she would have laid while sitting (her own production, not the farm's).
    const breedDef = HEN_BREEDS[hen.breed];
    const ownRate = Math.min(MAX_LAY_RATE, breedDef.layRate * mods.lay) * (breedDef.eggValue + mods.valueFlat) * mods.value;
    const reward = Math.max(BROOD_REWARD_FLOOR, ownRate * BROOD_DURATION_SEC * BROOD_REWARD_MULT * mods.hatchReward);
    const eggs = s.events.filter((x) => x.kind === 'broodEgg').sort((a, b) => a.spawnedAt - b.spawnedAt);
    while (eggs.length >= MAX_BROOD_EGGS_ON_SCREEN) {
      const old = eggs.shift()!;
      s.events.splice(s.events.indexOf(old), 1);
      earn(s, old.reward ?? 0); // evicted eggs pay out rather than vanish
    }
    spawnEvent(s, ctx, 'broodEgg', flock.id, { reward, breed: hen.breed, expiresAt: Infinity });
    emit(s, { kind: 'broodResult', success: true, breed: hen.breed, traits, reward });
    logActivity(s, 'event', 'Broody hatch', `${HEN_BREEDS[hen.breed].name}${traits.length ? ` gained ${traits.join(', ')}` : ''}`);
  } else {
    earn(s, BROOD_FAIL_CONSOLATION);
    emit(s, { kind: 'broodResult', success: false, breed: hen.breed, traits: [], reward: BROOD_FAIL_CONSOLATION });
  }
  hen.broodySince = null;
  hen.layingUntil = s.time + layingPhaseMs(hen.breed, flock);
  touch(s);
}
